import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  V2DataGrid,
  V2Badge,
  V2Button,
  V2EmptyState,
  V2Avatar,
  V2FormGrid,
  V2FormField,
  V2FormActions,
  Drawer,
  ConfirmDialog,
  StatePanel,
  Icon,
  Modal,
  Alert,
  LoadingState,
  type V2TableColumn,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { RoleEditorDrawer } from './RoleEditorDrawer.js';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import {
  IRAN_MOBILE_PATTERN,
  IRAN_MOBILE_VALIDATION_MESSAGE,
  isValidIranMobileNumber,
  toEnglishDigits,
} from '../../lib/phoneNumber.js';
import type {
  CustomRoleCreatedResponse,
  InvitationCreatedResponse,
  MembershipListResponse,
  MembershipSummary,
  PermissionCatalogGroup,
  PermissionCatalogResponse,
  RoleListResponse,
} from '../../lib/types/membership.js';
import type { SubscriptionResponse } from '../../lib/types/subscriptionSecurity.js';
import {
  effectiveInviteStatus,
  getDemoTeamRecord,
  INVITE_STATUS_LABEL,
  upsertDemoTeamRecord,
  type EffectiveInviteStatus,
} from '../../lib/demo/teamDirectoryAdapter.js';
import { generateInvitationText, type InviteModalData } from '../../lib/legacyTeamPresets.js';

interface AccessPreset {
  readonly title: string;
  readonly groupIds: readonly string[];
}

/** Same real `permissions/catalog` groups as `setup/TeamStep.tsx` — the
 * only presets a colleague's invite can ever resolve to. */
const ACCESS_PRESETS: readonly AccessPreset[] = [
  { title: 'مدیر فروش', groupIds: ['sales'] },
  { title: 'کارشناس فروش', groupIds: ['sales'] },
  { title: 'حسابدار', groupIds: ['finance'] },
  { title: 'انباردار', groupIds: ['inventory'] },
  { title: 'مسئول تدارکات', groupIds: ['procurement', 'inventory'] },
  { title: 'مدیر پروژه', groupIds: ['projects'] },
];

const GROUP_ICON: Record<
  string,
  'sales' | 'inventory' | 'cart' | 'finance' | 'projects' | 'shield'
> = {
  sales: 'sales',
  inventory: 'inventory',
  procurement: 'cart',
  finance: 'finance',
  projects: 'projects',
  admin: 'shield',
  'master-data': 'shield',
};

const MEMBERSHIP_STATUS_TONE: Record<string, V2BadgeTone> = {
  active: 'success',
  disabled: 'danger',
  pending: 'warn',
};
const MEMBERSHIP_STATUS_LABEL: Record<string, string> = {
  active: 'فعال',
  disabled: 'غیرفعال',
  pending: 'در انتظار پذیرش',
};

const INVITE_STATUS_TONE: Record<EffectiveInviteStatus, V2BadgeTone> = {
  invited: 'warn',
  accepted: 'success',
  expired_or_cancelled: 'neutral',
};

interface Row extends MembershipSummary {
  readonly id: string;
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Pilot P0 §2.1 کاربران و دسترسی‌ها (Users hub + invite drawer)
 * canonical pattern: V2Table + Drawer("ENTITYFORMDRAWER" kit frame) for the
 *   invite form — matches the design's real drawer pattern (PRODUCTION_
 *   READINESS_AUDIT.md's "Drawers are a real, already-used pattern" note).
 * state reference: ready · loading · permission_denied(403) · network_error
 *
 * Invite drawer / D-020 (ERP-245) status: PARTIAL_REAL. `mobile`,
 * `firstName`, `lastName` are real required fields, validated client-side
 * against the same Iranian-mobile shape the backend enforces
 * (`packages/auth/src/mobile.ts:isValidIranMobile` ~
 * `lib/phoneNumber.ts:isValidIranMobileNumber`) and marked `required` on
 * their `V2FormField`s. `roleId` is required once `mobile` is present, per
 * `assertTenantInvitationBody`
 * (`apps/api/src/membership/membership-admin.controller.ts:254-277`).
 *
 * BUG-01/BUG-04/BUG-21 fix: this form previously mapped its module
 * checkboxes (pre-checked to «مالی»/«خرید» by default) to one of two fixed
 * roles via a `wantsAdmin` heuristic — `tenant-operator` was never actually
 * seeded, so the fallback (`roles.data.roles[0]`) silently resolved to
 * whatever role happened to be first, which for a fresh tenant is always
 * `tenant-admin`. Every invite through this page therefore granted full
 * tenant-admin regardless of which checkboxes were picked. It now follows
 * the same real, already-correct pattern `setup/TeamStep.tsx` uses: no
 * module is pre-selected, and submitting creates a role scoped to exactly
 * the chosen `permissions/catalog` keys via `POST /api/v1/roles`, then
 * invites with that role's id. Full admin is still reachable, but only by
 * explicitly selecting every catalog key.
 *
 * Row "تغییر نقش" dropdown fix, 2026-10-05: the dropdown previously only
 * rendered when `getDemoTeamRecord(...).knownAccountId` was set — a
 * client-side, per-browser cache populated only at the moment this exact
 * browser sent the invite (see the removed comment on `DemoTeamRecord.
 * knownAccountId`). Reload the page, open it from another device, or look
 * at any member who accepted an invite sent earlier/elsewhere, and the
 * cache is empty — the row silently fell back to read-only badges with no
 * way to ever assign a role, including the exact `accessReviewRequired`
 * ("نیازمند تعیین دسترسی") members who need one most. Root cause: the
 * client used `POST/DELETE .../principals/:principalId/roles/:roleId`,
 * which takes a raw `accountId` the list endpoint never exposes (see
 * `lib/types/membership.ts`'s top comment). Fixed by switching to
 * `POST .../memberships/:memberRef/role` (`membership-admin.controller.ts`
 * `changeRole`, backed by `MembershipAdminService.changeRole` —
 * `packages/auth/src/membership-admin/service.ts:950`), which resolves the
 * membership's real `accountId` server-side from the same `memberRef`
 * already on every row and replaces its role assignment(s) with exactly
 * one new role in one call — no client-cached id, no separate
 * unassign-then-assign loop. The dropdown now renders for every non-owner
 * row unconditionally. The tenant-admin confirm step
 * (`pendingAdminAssign`/`ConfirmDialog`) is unchanged, and the server now
 * enforces it on this endpoint too (`confirmTenantAdmin` body field,
 * required when the target role is `tenant-admin` — added in the same
 * change; previously only the legacy `principals/:id/roles/:id` endpoint
 * enforced it, so switching endpoints would otherwise have silently
 * dropped that safety net).
 */
export function UsersPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://sakhtar.net';
  const loginUrl = `${origin}/t/${slug}/login`;

  const [refreshKey, setRefreshKey] = useState(0);
  const [demoVersion, setDemoVersion] = useState(0);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | EffectiveInviteStatus>('');

  // Drawer state for inviting new colleague
  const [inviteOpen, setInviteOpen] = useState(false);
  const [roleEditorOpen, setRoleEditorOpen] = useState(false);
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteMobile, setInviteMobile] = useState('');
  const [inviteJobTitle, setInviteJobTitle] = useState('');
  // Least privilege (BUG-01/BUG-21): no module is pre-selected — the admin
  // must explicitly grant each one.
  const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(new Set());
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Modal dialog state for invitation text
  const [modalOpen, setModalOpen] = useState(false);
  const [activeInviteData, setActiveInviteData] = useState<InviteModalData | null>(null);
  const [copied, setCopied] = useState(false);

  const [pendingDisable, setPendingDisable] = useState<Row | null>(null);
  const [pendingAdminAssign, setPendingAdminAssign] = useState<{
    readonly row: Row;
    readonly roleId: string;
    readonly displayName: string;
  } | null>(null);
  const [busyRef, setBusyRef] = useState<string | null>(null);
  const [rowNotice, setRowNotice] = useState<{ ref: string; text: string } | null>(null);

  const members = useApiGet<MembershipListResponse>(slug ? `${base}/api/v1/memberships` : null, [
    slug,
    refreshKey,
  ]);
  const roles = useApiGet<RoleListResponse>(slug ? `${base}/api/v1/roles` : null, [slug]);
  const catalog = useApiGet<PermissionCatalogResponse>(
    slug ? `${base}/api/v1/permissions/catalog` : null,
    [slug],
  );
  const groups: readonly PermissionCatalogGroup[] =
    catalog.status === 'ready' ? catalog.data.groups : [];
  const allKeys = useMemo(() => groups.flatMap((g) => g.keys), [groups]);
  const me = useApiGet<{
    firstName: string | null;
    lastName: string | null;
    mobile: string | null;
    roleDisplayNames?: string[];
  }>(slug ? `${base}/api/v1/me` : null, [slug]);

  const subscription = useApiGet<SubscriptionResponse>(
    slug ? `${base}/api/v1/admin/subscription` : null,
    [slug],
  );
  const seatUsage =
    subscription.status === 'ready' && subscription.data.phase === 'ready'
      ? (subscription.data.subscription?.seat ?? null)
      : null;

  const bumpDemo = () => setDemoVersion((v) => v + 1);

  const rows: Row[] = useMemo(() => {
    if (members.status !== 'ready') return [];
    return members.data.memberships.map((m) => ({ ...m, id: m.memberRef }));
  }, [members]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      const demo = slug ? getDemoTeamRecord(slug, row.memberRef) : {};
      if (roleFilter && !row.roleDisplayNames.includes(roleFilter)) return false;
      if (statusFilter && effectiveInviteStatus(row.membershipStatus, demo) !== statusFilter) {
        return false;
      }
      if (q) {
        const haystack = [
          demo.displayName,
          demo.jobTitle,
          demo.email,
          demo.department,
          demo.knownLoginIdentifier,
          row.loginIdentifier,
          row.displayName,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [rows, query, roleFilter, statusFilter, slug, demoVersion]);

  const allRoleNames = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) for (const name of row.roleDisplayNames) set.add(name);
    return [...set];
  }, [rows]);

  if (!slug) return null;

  function toggleGroup(group: PermissionCatalogGroup) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const groupIsFullyOn = group.keys.every((k) => next.has(k));
      for (const key of group.keys) {
        if (groupIsFullyOn) next.delete(key);
        else next.add(key);
      }
      return next;
    });
  }

  function applyPreset(preset: AccessPreset) {
    setInviteJobTitle(preset.title);
    const keys = groups.filter((g) => preset.groupIds.includes(g.id)).flatMap((g) => g.keys);
    setSelectedKeys(new Set(keys));
  }

  function copyInviteText() {
    if (!activeInviteData) return;
    const text = generateInvitationText(activeInviteData);
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  }

  function resetInviteForm() {
    setInviteFirstName('');
    setInviteLastName('');
    setInviteMobile('');
    setInviteJobTitle('');
    setSelectedKeys(new Set());
    setInviteError(null);
  }

  async function submitInvite() {
    setInviteError(null);
    const fName = inviteFirstName.trim();
    const lName = inviteLastName.trim();
    const identifier = toEnglishDigits(inviteMobile).trim();
    const title = inviteJobTitle.trim();

    if (!fName || !lName) {
      setInviteError('لطفاً نام و نام خانوادگی همکار را وارد کنید.');
      return;
    }
    if (!isValidIranMobileNumber(identifier)) {
      setInviteError(IRAN_MOBILE_VALIDATION_MESSAGE);
      return;
    }
    if (!title) {
      setInviteError('لطفاً سمت سازمانی همکار را وارد کنید.');
      return;
    }
    if (selectedKeys.size === 0) {
      setInviteError('لطفاً حداقل دسترسی به یک بخش از سیستم را برای همکار خود انتخاب کنید.');
      return;
    }

    setSubmitting(true);
    try {
      // BUG-01/BUG-04 fix: a colleague's invite must never resolve to an
      // existing, possibly over-privileged role by guesswork (the previous
      // `tenant-operator` fallback silently landed on `roles.data.roles[0]`
      // — the only role a fresh tenant actually has, `tenant-admin` — for
      // every invite, regardless of the checkboxes chosen). Instead, create
      // a role scoped to exactly the selected permission keys, the same
      // real `POST /api/v1/roles` contract `setup/TeamStep.tsx` already uses.
      const role = await apiPost<CustomRoleCreatedResponse>(`${base}/api/v1/roles`, {
        displayName: title,
        permissionKeys: [...selectedKeys],
      });
      const result = await apiPost<InvitationCreatedResponse>(`${base}/api/v1/invitations`, {
        mobile: identifier,
        firstName: fName,
        lastName: lName,
        roleId: role.roleId,
      });

      const permissionLabels = groups
        .filter((g) => g.keys.some((k) => selectedKeys.has(k)))
        .map((g) => g.label);

      const inviteData: InviteModalData = {
        firstName: fName,
        lastName: lName,
        mobile: identifier,
        jobTitle: title,
        permissions: permissionLabels,
        loginUrl,
      };

      if (result.memberRef) {
        upsertDemoTeamRecord(slug!, result.memberRef, {
          displayName: `${fName} ${lName}`,
          department: title,
          jobTitle: title,
          knownLoginIdentifier: identifier,
          permissions: permissionLabels,
        });
      }

      setActiveInviteData(inviteData);
      setInviteOpen(false);
      setModalOpen(true);
      resetInviteForm();
      setRefreshKey((k) => k + 1);
      bumpDemo();
    } catch (err) {
      setInviteError(
        err instanceof ApiError && err.status === 409
          ? 'این شماره همراه قبلاً در این سازمان ثبت شده است (کاربر فعال، غیرفعال یا دعوت منقضی). در فهرست کاربران آن را پیدا کنید و از «ارسال مجدد دعوت» یا «فعال‌سازی» استفاده کنید.'
          : err instanceof ApiError && err.status === 403
            ? err.message || 'سقف مجاز کاربران پلن شما تکمیل شده یا دسترسی دعوت کاربر را ندارید.'
            : err instanceof ApiError && err.status === 400
              ? 'اطلاعات واردشده معتبر نیست؛ شماره همراه (مثل 09123456789)، نام و نام خانوادگی را بررسی کنید.'
              : 'ارسال دعوت ناموفق بود. دوباره تلاش کنید.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  function onInviteFormSubmit(event: FormEvent) {
    event.preventDefault();
    void submitInvite();
  }

  async function onConfirmDisable() {
    if (!pendingDisable) return;
    setBusyRef(pendingDisable.memberRef);
    try {
      await apiPost(`${base}/api/v1/memberships/${pendingDisable.memberRef}/disable`);
    } finally {
      setPendingDisable(null);
      setBusyRef(null);
      setRefreshKey((k) => k + 1);
    }
  }

  async function onReactivate(row: Row) {
    setBusyRef(row.memberRef);
    try {
      await apiPost(`${base}/api/v1/memberships/${row.memberRef}/reactivate`);
    } finally {
      setBusyRef(null);
      setRefreshKey((k) => k + 1);
    }
  }

  async function onResendInvite(row: Row) {
    const demo = getDemoTeamRecord(slug!, row.memberRef);
    const identifier = demo.knownLoginIdentifier;
    if (!identifier) return;
    setBusyRef(row.memberRef);
    setRowNotice(null);
    try {
      const result = await apiPost<InvitationCreatedResponse>(`${base}/api/v1/invitations`, {
        loginIdentifier: identifier,
      });
      setRowNotice({
        ref: row.memberRef,
        text: result.duplicate
          ? 'این دعوت هنوز معتبر است — پیام تازه‌ای ارسال نشد.'
          : 'دعوت جدید ثبت شد.',
      });
    } catch (err) {
      setRowNotice({
        ref: row.memberRef,
        text:
          err instanceof ApiError && err.status === 409
            ? 'این دعوت دیگر قابل ارسال مجدد نیست (منقضی یا استفاده‌شده). یک دعوت تازه ایجاد کنید.'
            : 'ارسال مجدد ناموفق بود.',
      });
    } finally {
      setBusyRef(null);
    }
  }

  function onCancelInviteLocally(row: Row) {
    upsertDemoTeamRecord(slug!, row.memberRef, { cancelledLocally: true });
    bumpDemo();
  }

  async function onChangeRole(row: Row, newRoleId: string, confirmedTenantAdmin = false) {
    if (!newRoleId || roles.status !== 'ready') return;
    const nextRole = roles.data.roles.find((r) => r.roleId === newRoleId);
    if (nextRole?.roleKey === 'tenant-admin' && !confirmedTenantAdmin) {
      setPendingAdminAssign({
        row,
        roleId: newRoleId,
        displayName: nextRole.displayName,
      });
      return;
    }
    setBusyRef(row.memberRef);
    setRowNotice(null);
    try {
      await apiPost(`${base}/api/v1/memberships/${row.memberRef}/role`, {
        roleId: newRoleId,
        ...(nextRole?.roleKey === 'tenant-admin' ? { confirmTenantAdmin: true } : {}),
      });
      setRefreshKey((k) => k + 1);
    } catch {
      setRowNotice({ ref: row.memberRef, text: 'تغییر دسترسی ناموفق بود — دوباره تلاش کنید.' });
    } finally {
      setBusyRef(null);
    }
  }

  const columns: readonly V2TableColumn<Row>[] = [
    {
      key: 'user',
      header: 'کاربر',
      render: (row) => {
        const demo = getDemoTeamRecord(slug, row.memberRef);
        const isOwner = row.roleDisplayNames.some((r) => r.toLowerCase().includes('admin'));

        let ownerFallbackName: string | undefined;
        if (isOwner && me.status === 'ready') {
          const parts = [me.data.firstName, me.data.lastName].filter(Boolean);
          if (parts.length > 0) ownerFallbackName = parts.join(' ');
        }

        const displayName =
          demo.displayName ||
          row.displayName ||
          ownerFallbackName ||
          (isOwner ? 'مدیر سیستم / مالک سازمان' : null);

        const initials = displayName
          ? displayName.trim().slice(0, 2)
          : isOwner
            ? 'مد'
            : 'کاربر'.slice(0, 2);

        const subTitle = demo.jobTitle || demo.department || (isOwner ? 'مدیریت ارشد' : null);

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <V2Avatar initials={initials} size="sm" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={{ fontWeight: 600, fontSize: 'var(--sk-fs-sm)' }}>
                {displayName ?? (
                  <span style={{ color: 'var(--sk-fg-subtle)' }}>بدون نام ثبت‌شده</span>
                )}
              </span>
              {subTitle ? (
                <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>{subTitle}</span>
              ) : demo.email ? (
                <span className="ltr" style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>
                  {demo.email}
                </span>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      key: 'mobile',
      header: 'شماره همراه',
      render: (row) => {
        const demo = getDemoTeamRecord(slug, row.memberRef);
        const isOwner = row.roleDisplayNames.some((r) => r.toLowerCase().includes('admin'));
        const ownerMobile = isOwner && me.status === 'ready' ? me.data.mobile : null;
        const value = row.loginIdentifier ?? demo.knownLoginIdentifier ?? ownerMobile;
        return value ? (
          <span
            className="ltr sk-num"
            style={{ fontFamily: 'var(--sk-font-mono)', fontSize: 13, direction: 'ltr' }}
          >
            {value}
          </span>
        ) : (
          <span style={{ color: 'var(--sk-fg-subtle)' }}>—</span>
        );
      },
    },
    {
      key: 'department',
      header: 'واحد / سمت سازمانی',
      render: (row) => {
        const demo = getDemoTeamRecord(slug, row.memberRef);
        const isOwner = row.roleDisplayNames.some((r) => r.toLowerCase().includes('admin'));
        const dept = demo.jobTitle || demo.department || (isOwner ? 'مدیریت ارشد' : null);
        return dept ? <span>{dept}</span> : <span style={{ color: 'var(--sk-fg-subtle)' }}>—</span>;
      },
    },
    {
      key: 'role',
      header: 'نقش و دسترسی‌ها',
      render: (row) => {
        const demo = getDemoTeamRecord(slug, row.memberRef);
        const isOwner = row.roleDisplayNames.some((r) => r.toLowerCase().includes('admin'));
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {row.accessReviewRequired ? <V2Badge tone="warn">نیازمند تعیین دسترسی</V2Badge> : null}
            {!isOwner && roles.status === 'ready' ? (
              <select
                className="sk-select"
                style={{ fontSize: 12, maxWidth: 170 }}
                value=""
                disabled={busyRef === row.memberRef}
                onChange={(e) => {
                  if (e.target.value) void onChangeRole(row, e.target.value);
                }}
              >
                <option value="" disabled>
                  {row.roleDisplayNames.join('، ') || 'بدون نقش'}
                </option>
                {roles.data.roles
                  .filter((r) => r.roleKey !== 'bug01-corrected-invited-member')
                  .map((r) => (
                    <option key={r.roleId} value={r.roleId}>
                      تغییر به: {r.displayName}
                    </option>
                  ))}
              </select>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {row.roleDisplayNames.length > 0 ? (
                  row.roleDisplayNames.map((name) => (
                    <span key={name} className="sk-tag" style={{ fontWeight: 600 }}>
                      {name}
                    </span>
                  ))
                ) : (
                  <span style={{ color: 'var(--sk-fg-subtle)' }}>بدون نقش</span>
                )}
              </div>
            )}
            {row.accessReviewRequired ? (
              <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                هنوز دسترسی عملیاتی برای این کاربر تعیین نشده است.
              </span>
            ) : demo.permissions && demo.permissions.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                {demo.permissions.map((p) => (
                  <V2Badge key={p} tone="neutral">
                    {p}
                  </V2Badge>
                ))}
              </div>
            ) : isOwner ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                <V2Badge tone="neutral">دسترسی کامل سیستم</V2Badge>
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'inviteStatus',
      header: 'وضعیت دعوت',
      render: (row) => {
        const demo = getDemoTeamRecord(slug, row.memberRef);
        const status = effectiveInviteStatus(row.membershipStatus, demo);
        return <V2Badge tone={INVITE_STATUS_TONE[status]}>{INVITE_STATUS_LABEL[status]}</V2Badge>;
      },
    },
    {
      key: 'membershipStatus',
      header: 'وضعیت عضویت',
      render: (row) => (
        <V2Badge tone={MEMBERSHIP_STATUS_TONE[row.membershipStatus] ?? 'neutral'}>
          {MEMBERSHIP_STATUS_LABEL[row.membershipStatus] ?? row.membershipStatus}
        </V2Badge>
      ),
    },
    {
      key: 'actions',
      header: 'عملیات',
      align: 'end',
      render: (row) => {
        const demo = getDemoTeamRecord(slug, row.memberRef);
        const canResend = Boolean(demo.knownLoginIdentifier);
        const isPending = row.membershipStatus === 'pending';
        const isBusy = busyRef === row.memberRef;
        const isOwner = row.roleDisplayNames.some((r) => r.toLowerCase().includes('admin'));

        const fullName = demo.displayName || row.displayName || 'همکار گرامی';
        const [rowFirstName, ...restLastName] = fullName.split(' ');
        const rowLastName = restLastName.join(' ');
        const phone = row.loginIdentifier ?? demo.knownLoginIdentifier ?? '';
        const title = demo.jobTitle || demo.department || 'عضو سازمان';
        const perms =
          demo.permissions && demo.permissions.length > 0 ? demo.permissions : ['عمومی'];

        const inviteModalData: InviteModalData = {
          firstName: rowFirstName || fullName,
          lastName: rowLastName || '',
          mobile: phone,
          jobTitle: title,
          permissions: perms,
          loginUrl,
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {phone ? (
                <V2Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setActiveInviteData(inviteModalData);
                    setModalOpen(true);
                  }}
                  title="مشاهده و کپی متن دعوت‌نامه برای ارسال در پیام‌رسان‌ها"
                >
                  مشاهده دعوت‌نامه
                </V2Button>
              ) : null}
              {isPending ? (
                <V2Button
                  variant="ghost"
                  size="sm"
                  disabled={!canResend || isBusy}
                  title={
                    canResend
                      ? undefined
                      : 'شماره همراه این دعوت در این مرورگر ثبت نشده — فقط برای دعوت‌های همین نشست ممکن است.'
                  }
                  onClick={() => void onResendInvite(row)}
                >
                  ارسال دعوت مجدد
                </V2Button>
              ) : null}
              {isPending && !demo.cancelledLocally ? (
                <V2Button variant="ghost" size="sm" onClick={() => onCancelInviteLocally(row)}>
                  لغو دعوت (نمایشی)
                </V2Button>
              ) : null}
              {row.membershipStatus === 'active' ? (
                <V2Button
                  variant="danger"
                  size="sm"
                  disabled={isBusy || isOwner}
                  title={isOwner ? 'حساب مدیر ارشد سازمان قابل غیرفعال‌سازی نیست.' : undefined}
                  onClick={() => setPendingDisable(row)}
                >
                  غیرفعال‌سازی
                </V2Button>
              ) : row.membershipStatus === 'disabled' ? (
                <V2Button
                  variant="secondary"
                  size="sm"
                  disabled={isBusy}
                  onClick={() => void onReactivate(row)}
                >
                  فعال‌سازی مجدد
                </V2Button>
              ) : null}
            </div>
            {rowNotice?.ref === row.memberRef ? (
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--sk-fg-muted)',
                  maxWidth: 220,
                  textAlign: 'left',
                }}
              >
                {rowNotice.text}
              </span>
            ) : null}
          </div>
        );
      },
    },
  ];

  const memberCount = members.status === 'ready' ? members.data.memberships.length : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>
            کاربران و دسترسی‌ها
          </h1>
          {members.status === 'ready' ? (
            <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
              {memberCount.toLocaleString('fa-IR')} کاربر
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <V2Button
            variant="secondary"
            icon="shield"
            onClick={() => setRoleEditorOpen(true)}
            disabled={roles.status !== 'ready' || catalog.status !== 'ready'}
          >
            ویرایش دسترسی نقش‌ها
          </V2Button>
          <V2Button variant="primary" icon="plus" onClick={() => setInviteOpen(true)}>
            دعوت همکار جدید
          </V2Button>
        </div>
      </div>

      {members.status === 'loading' ? (
        <StatePanel variant="loading" title="در حال بارگذاری کاربران" />
      ) : null}
      {members.status === 'error' ? (
        <StatePanel
          variant={members.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={members.error.status === 403 ? 'دسترسی ندارید' : 'فهرست کاربران قابل نمایش نیست'}
          action={
            members.error.status === 403 ? undefined : (
              <V2Button variant="secondary" size="sm" onClick={() => setRefreshKey((k) => k + 1)}>
                تلاش دوباره
              </V2Button>
            )
          }
        />
      ) : null}

      {members.status === 'ready' ? (
        <V2DataGrid
          icon="users"
          title="کاربران و دسترسی‌ها"
          subtitle={`${filteredRows.length.toLocaleString('fa-IR')} از ${memberCount.toLocaleString('fa-IR')} عضو`}
          search={{ value: query, onChange: setQuery, placeholder: 'نام، ایمیل یا شماره همراه' }}
          filters={[
            {
              id: 'invite',
              label: 'وضعیت دعوت',
              value: statusFilter,
              onChange: (v) => setStatusFilter(v as '' | EffectiveInviteStatus),
              options: [
                { value: '', label: 'همه' },
                ...(Object.keys(INVITE_STATUS_LABEL) as EffectiveInviteStatus[]).map((key) => ({
                  value: key,
                  label: INVITE_STATUS_LABEL[key],
                })),
              ],
            },
          ]}
          toolbarExtra={
            <select
              className="sk-select"
              aria-label="نقش"
              style={{ width: 160, height: 32 }}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">همهٔ نقش‌ها</option>
              {allRoleNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          }
          columns={columns}
          rows={filteredRows}
          rowKey={(r) => r.id}
          zebra
          emptyState={
            memberCount === 0 ? (
              <V2EmptyState
                icon="users"
                title="هنوز همکاری دعوت نشده"
                description="با «دعوت همکار جدید» اولین عضو تیم خود را اضافه کنید."
                action={
                  <V2Button
                    variant="primary"
                    size="sm"
                    icon="plus"
                    onClick={() => setInviteOpen(true)}
                  >
                    دعوت همکار جدید
                  </V2Button>
                }
              />
            ) : (
              <V2EmptyState icon="search" title="نتیجه‌ای برای این فیلتر یافت نشد" compact />
            )
          }
        />
      ) : null}

      <RoleEditorDrawer
        open={roleEditorOpen}
        base={base}
        roles={
          roles.status === 'ready'
            ? roles.data.roles.filter((r) => r.roleKey.startsWith('custom-'))
            : []
        }
        groups={groups}
        onClose={() => setRoleEditorOpen(false)}
        onSaved={roles.reload}
      />

      {/* Drawer: دعوت همکار جدید با دسترسی‌های جزئی */}
      <Drawer
        open={inviteOpen}
        title="دعوت همکار جدید"
        onClose={() => {
          setInviteOpen(false);
          resetInviteForm();
        }}
        footer={
          <V2FormActions>
            <V2Button
              variant="secondary"
              onClick={() => {
                setInviteOpen(false);
                resetInviteForm();
              }}
              disabled={submitting}
            >
              انصراف
            </V2Button>
            <V2Button variant="primary" onClick={() => void submitInvite()} disabled={submitting}>
              {submitting ? 'در حال ارسال…' : 'ارسال دعوت و ایجاد دسترسی'}
            </V2Button>
          </V2FormActions>
        }
      >
        <form
          onSubmit={onInviteFormSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
        >
          {/* ۱. مشخصات فردی و سازمانی */}
          <V2FormGrid>
            <V2FormField label="نام" required width="medium">
              <input
                className="sk-input"
                placeholder="مثال: علی"
                value={inviteFirstName}
                onChange={(e) => setInviteFirstName(e.target.value)}
                disabled={submitting}
                required
              />
            </V2FormField>

            <V2FormField label="نام خانوادگی" required width="medium">
              <input
                className="sk-input"
                placeholder="مثال: محمدی"
                value={inviteLastName}
                onChange={(e) => setInviteLastName(e.target.value)}
                disabled={submitting}
                required
              />
            </V2FormField>

            <V2FormField label="شماره همراه" required width="full">
              <input
                className="sk-input ltr"
                type="tel"
                inputMode="numeric"
                dir="ltr"
                pattern={IRAN_MOBILE_PATTERN}
                maxLength={11}
                placeholder="09xxxxxxxxx"
                value={inviteMobile}
                onChange={(e) => setInviteMobile(toEnglishDigits(e.target.value))}
                disabled={submitting}
                required
              />
            </V2FormField>

            <V2FormField label="سمت سازمانی" required width="full">
              <input
                className="sk-input"
                placeholder="مثال: حسابدار، مدیر فروش، انباردار..."
                value={inviteJobTitle}
                onChange={(e) => setInviteJobTitle(e.target.value)}
                disabled={submitting}
                required
              />
            </V2FormField>
          </V2FormGrid>

          {/* الگوهای سازمانی پیشنهادی */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              padding: '10px 14px',
              background: 'var(--sk-bg-canvas)',
              borderRadius: 'var(--sk-r-md)',
              border: '1px solid var(--sk-border)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--sk-fs-xs)',
                fontWeight: 600,
                color: 'var(--sk-fg-muted)',
              }}
            >
              الگوهای سازمانی پیشنهادی:
            </span>
            {ACCESS_PRESETS.map((p) => (
              <button
                key={p.title}
                type="button"
                onClick={() => applyPreset(p)}
                disabled={submitting}
                style={{
                  cursor: 'pointer',
                  fontSize: 'var(--sk-fs-xs)',
                  padding: '4px 10px',
                  borderRadius: 'var(--sk-r-pill)',
                  border: `1px solid ${inviteJobTitle === p.title ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                  background:
                    inviteJobTitle === p.title ? 'var(--sk-brand-500)' : 'var(--sk-bg-elev)',
                  color: inviteJobTitle === p.title ? 'var(--sk-fg-on-brand)' : 'var(--sk-fg)',
                  transition: 'all 0.15s ease',
                }}
              >
                {p.title}
              </button>
            ))}
          </div>

          {/* ۲. تعیین دسترسی‌های ماژول‌ها تک‌به‌تک */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: 'var(--sk-fs-sm)',
                    fontWeight: 600,
                    color: 'var(--sk-fg)',
                  }}
                >
                  دسترسی‌های بخش‌های سامانه (تک‌به‌تک) *
                </span>
                <div
                  style={{
                    fontSize: 'var(--sk-fs-xs)',
                    color: 'var(--sk-fg-muted)',
                    marginTop: 2,
                  }}
                >
                  بخش‌هایی که این همکار مجاز به کار با آن‌ها خواهد بود:
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="sk-btn sk-btn--ghost sk-btn--sm"
                  onClick={() => setSelectedKeys(new Set(allKeys))}
                  disabled={submitting || allKeys.length === 0}
                >
                  انتخاب همه
                </button>
                <button
                  type="button"
                  className="sk-btn sk-btn--ghost sk-btn--sm"
                  onClick={() => setSelectedKeys(new Set())}
                  disabled={submitting}
                >
                  حذف همه
                </button>
              </div>
            </div>

            {catalog.status === 'loading' ? <LoadingState /> : null}

            {/* ماتریس کارت‌های دسترسی ماژول‌ها — گروه‌های واقعی permissions/catalog */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 10,
              }}
            >
              {groups.map((group) => {
                const isChecked =
                  group.keys.length > 0 && group.keys.every((k) => selectedKeys.has(k));
                return (
                  <div
                    key={group.id}
                    onClick={() => toggleGroup(group)}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '12px 14px',
                      borderRadius: 'var(--sk-r-md)',
                      border: `1.5px solid ${isChecked ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                      background: isChecked
                        ? 'color-mix(in oklch, var(--sk-brand-500) 6%, var(--sk-bg-elev))'
                        : 'var(--sk-bg-elev)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      boxShadow: isChecked ? 'var(--sk-shadow-1)' : undefined,
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      className="sk-check"
                      checked={isChecked}
                      onChange={() => toggleGroup(group)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={submitting}
                      style={{ marginTop: 2 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon name={GROUP_ICON[group.id] ?? 'shield'} size="sm" />
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 'var(--sk-fs-sm)',
                            color: isChecked ? 'var(--sk-brand-600)' : 'var(--sk-fg)',
                          }}
                        >
                          {group.label}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {seatUsage ? (
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginTop: 4 }}>
              با ارسال این دعوت ۱ صندلی از {seatUsage.available.toLocaleString('fa-IR')} صندلی آزاد
              اشغال می‌شود.
            </div>
          ) : null}

          {inviteError ? (
            <Alert tone="danger" title="خطا در ارسال دعوت">
              {inviteError}
            </Alert>
          ) : null}
        </form>
      </Drawer>

      {/* مدال و دیالوگ اختصاصی نمایش و کپی متن دعوت‌نامه */}
      {activeInviteData && (
        <Modal
          open={modalOpen}
          title="متن دعوت‌نامه همکار"
          onClose={() => setModalOpen(false)}
          footer={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <V2Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setModalOpen(false);
                  resetInviteForm();
                  setInviteOpen(true);
                }}
              >
                افزودن عضو دیگر
              </V2Button>
              <div style={{ display: 'flex', gap: 8 }}>
                <V2Button variant="ghost" size="sm" onClick={() => setModalOpen(false)}>
                  بستن
                </V2Button>
                <V2Button variant="primary" size="sm" icon="check" onClick={copyInviteText}>
                  {copied ? 'متن کپی شد! ✓' : 'کپی متن دعوت‌نامه'}
                </V2Button>
              </div>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Alert tone="success">
              دعوت با موفقیت ثبت شد و پیامک اطلاع‌رسانی ارسال گردید. همچنین می‌توانید متن زیر را کپی
              کرده و از طریق پیام‌رسان‌ها (واتساپ، تلگرام، بله، ایتا و...) برای همکار خود ارسال
              کنید:
            </Alert>

            <div
              style={{
                background: 'var(--sk-bg-canvas)',
                border: '1px solid var(--sk-border)',
                borderRadius: 'var(--sk-r-md)',
                padding: '16px 18px',
                fontSize: 'var(--sk-fs-sm)',
                lineHeight: 1.8,
                whiteSpace: 'pre-wrap',
                color: 'var(--sk-fg)',
                userSelect: 'all',
                position: 'relative',
              }}
            >
              {generateInvitationText(activeInviteData)}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <V2Button variant="secondary" size="sm" icon="check" onClick={copyInviteText}>
                {copied ? 'متن در کلیپ‌بورد کپی شد! ✓' : 'کپی متن پیام دعوت‌نامه'}
              </V2Button>
            </div>
          </div>
        </Modal>
      )}

      {/* مدال تأیید غیرفعال‌سازی کاربر */}
      {pendingDisable ? (
        <ConfirmDialog
          open
          title="غیرفعال‌سازی کاربر"
          message={`آیا از غیرفعال‌سازی کاربر ${pendingDisable.displayName || pendingDisable.loginIdentifier || ''} اطمینان دارید؟ نشست‌های فعال این کاربر لغو خواهد شد.`}
          danger
          confirmLabel="غیرفعال‌سازی"
          cancelLabel="انصراف"
          onConfirm={() => void onConfirmDisable()}
          onCancel={() => setPendingDisable(null)}
        />
      ) : null}

      {pendingAdminAssign ? (
        <ConfirmDialog
          open
          title="اعطای نقش مدیر سازمان"
          message={`نقش «${pendingAdminAssign.displayName}» دسترسی کامل مدیریت سازمان را می‌دهد و باید به‌صورت صریح انتخاب شود. آیا از این انتخاب اطمینان دارید؟`}
          danger
          confirmLabel="تأیید و اعطا"
          cancelLabel="انصراف"
          onConfirm={() => {
            const pending = pendingAdminAssign;
            setPendingAdminAssign(null);
            void onChangeRole(pending.row, pending.roleId, true);
          }}
          onCancel={() => setPendingAdminAssign(null)}
        />
      ) : null}
    </div>
  );
}
